// client/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import tokenService from '../services/tokenService';
import API_BASE_URL from '../config/api.config';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Motivo dell'ultimo logout automatico, letto dalla pagina di login ('idle' | 'expired')
export const LOGOUT_REASON_KEY = 'logoutReason';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Set axios authorization header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Clear the session locally (no server call)
  const clearSession = useCallback((reason) => {
    tokenService.cleanup();
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];

    if (reason) {
      try {
        sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
      } catch (e) {
        // ignore
      }
    }
  }, []);

  // Logout user
  // Senza reason è il logout manuale: invalida i token anche lato server.
  // Con reason ('idle' | 'expired') è un logout automatico: il token è già scaduto
  // o sta per scadere, e non serve disconnettere gli altri dispositivi.
  const logout = useCallback((reason) => {
    const currentToken = localStorage.getItem('token');
    if (!reason && currentToken) {
      axios.post(`${API_BASE_URL}/auth/logout`, null, {
        headers: { Authorization: `Bearer ${currentToken}` }
      }).catch(() => {});
    }
    clearSession(reason);
  }, [clearSession]);

  // Any 401 from the API means the session is no longer valid
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        if (
          error.response?.status === 401 &&
          localStorage.getItem('token') &&
          !url.includes('/auth/login') &&
          !url.includes('/auth/logout')
        ) {
          clearSession('expired');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [clearSession]);

  // Sync with other tabs: logout and token refresh done in another tab
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== 'token') return;

      if (!event.newValue) {
        clearSession();
      } else {
        setToken(event.newValue);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [clearSession]);

  // Start token refresh when user is authenticated
  useEffect(() => {
    if (token && user && initialized) {
      // Start automatic token refresh
      tokenService.startTokenRefresh(
        // Success callback
        (newToken, userData) => {
          setToken(newToken);
          localStorage.setItem('token', newToken);

          if (userData) {
            setUser(prevUser => ({
              ...prevUser,
              ...userData
            }));
          }
        },
        // Error callback: session ended server side (max duration, logout elsewhere)
        () => {
          clearSession('expired');
        }
      );
    } else {
      // Stop token refresh when user is not authenticated
      tokenService.stopTokenRefresh();
    }

    // Cleanup on unmount
    return () => {
      tokenService.stopTokenRefresh();
    };
  }, [token, user, initialized, clearSession]);

  // Track user activity globally: only real interaction counts,
  // API calls (e.g. background polling) must not keep the session alive
  useEffect(() => {
    if (token && user) {
      const handleUserActivity = () => {
        tokenService.updateActivity();
      };

      const activityEvents = [
        'mousedown', 'mousemove', 'keypress',
        'scroll', 'touchstart', 'click'
      ];

      // Add event listeners
      activityEvents.forEach(event => {
        window.addEventListener(event, handleUserActivity, true);
      });

      // Cleanup
      return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleUserActivity, true);
        });
      };
    }
  }, [token, user]);

  // Load User
  useEffect(() => {
    let cancelled = false;
    let isLoading = false; // Previene chiamate multiple

    const loadUser = async () => {
      // Se stiamo già caricando, esci
      if (isLoading) {
        return;
      }

      if (token && !user && !cancelled) {
        isLoading = true; // Imposta flag di caricamento
        setLoading(true);

        try {
          const res = await axios.get(`${API_BASE_URL}/auth/profile`);

          if (!cancelled && res.data?.user) {
            setUser(res.data.user);
          }
        } catch (err) {
          if (!cancelled) {
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
              localStorage.removeItem('token');
              setToken(null);
              setUser(null);
            }
            // Log solo in development
            if (process.env.NODE_ENV === 'development') {
              console.error('Auth error:', err.message);
            }
            // Non esporre altri errori all'utente
          }
        } finally {
          if (!cancelled) {
            isLoading = false;
            setLoading(false);
            setInitialized(true);
          }
        }
      } else if (!token) {
        // Nessun token, imposta stato inizializzato
        if (!cancelled) {
          setLoading(false);
          setInitialized(true);
          setUser(null);
        }
      } else if (user) {
        // User già presente, assicurati che sia inizializzato
        if (!cancelled && !initialized) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    loadUser();

    return () => {
      cancelled = true;
    };
  }, [token, user, initialized]); // Aggiungi initialized alle dipendenze

  // Login user - returns a Promise to allow waiting for completion
  const login = async (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);

    // Set authorization header immediately
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    // Initialize activity time for new session
    tokenService.updateActivity();
    try {
      sessionStorage.removeItem(LOGOUT_REASON_KEY);
    } catch (e) {
      // ignore
    }

    // Return a resolved promise to make this awaitable
    return Promise.resolve();
  };

  // Update user profile
  const updateProfile = (updatedUser) => {
    setUser({ ...user, ...updatedUser });
  };

  // Manual token refresh
  const refreshToken = async () => {
    try {
      const newToken = await tokenService.manualRefresh();
      return newToken;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Manual token refresh failed:', error);
      }
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        updateProfile,
        refreshToken,
        isAuthenticated: !!token,
        initialized
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
